import { Observable } from '@apollo/client';

/**
 * Códigos del backend cuyo contrato es «aborto transitorio con rollback total —
 * reintentar es seguro» (`rois-back/src/utils/business-error.helper.ts` + el
 * mapeo global de su `formatError`).
 *
 * Viven en su propio módulo, sin dependencias, porque hay DOS consumidores y
 * duplicar la lista sería garantizar que un día se separen:
 *
 *  - `gql/client.tsx` — el `errorLink` reintenta UNA vez la misma operación.
 *  - `offline/offlineImageUploadSyncManager.ts` — la cola NO puede marcarlos
 *    como veneno: son transitorios y la operación es válida.
 *
 * Por qué importa (incidente 2026-08-10, subida de 30 fotos): la cola trataba
 * cualquier error GraphQL como veneno permanente, así que un `SAVE_TX_CONFLICT`
 * —que MongoDB clasifica explícitamente como `TransientTransactionError` y la
 * propia documentación dice que hay que reintentar— dejaba la foto muerta con
 * un «no se pudieron subir» que exigía pulsar Reintentar a mano.
 */
export const RETRYABLE_BACKEND_CODES: ReadonlySet<string> = new Set([
  'SAVE_TX_TIMEOUT', // P2028 — timeout de la $transaction (red lenta)
  'SAVE_TX_CONFLICT', // P2034 — write conflict / deadlock
  'DB_TRANSIENT_ERROR', // socket Mongo roto/timeout (Broken pipe…)
]);

/**
 * ¿El error trae uno de esos códigos?
 *
 * Mira en los dos sitios por los que puede llegar: el `extensions` de un
 * GraphQLError individual (lo que ve el `errorLink`) y el array `graphQLErrors`
 * de un ApolloError ya montado (lo que ve la cola). El contrato estable es
 * `extensions.originalError.code`; `extensions.code` lo pone el driver de Nest
 * y varía, así que se comprueban ambos.
 */
export function esErrorTransitorioDeBackend(error: unknown): boolean {
  return backendErrorCodes(error).some((code) => RETRYABLE_BACKEND_CODES.has(code));
}

/** El código de negocio de un `extensions` (el contrato estable va primero). */
function codeOf(extensions: unknown): string | undefined {
  const e = extensions as
    | { code?: string; originalError?: { code?: string } }
    | undefined;
  return e?.originalError?.code ?? e?.code;
}

/**
 * Todos los códigos de negocio que trae un error, miren donde miren: el
 * `extensions` de un GraphQLError suelto, el `graphQLErrors` de un ApolloError
 * y, si el servidor contestó con un HTTP 4xx, el `errors[]` del cuerpo.
 */
export function backendErrorCodes(error: unknown): string[] {
  const err = error as {
    extensions?: unknown;
    graphQLErrors?: Array<{ extensions?: unknown }>;
    networkError?: { result?: { errors?: Array<{ extensions?: unknown }> } };
  };
  const errors = [
    err,
    ...(err?.graphQLErrors ?? []),
    ...(err?.networkError?.result?.errors ?? []),
  ];
  return errors
    .map((e) => codeOf(e?.extensions))
    .filter((code): code is string => typeof code === 'string');
}

/** ¿El error trae ESTE código de negocio? */
export function hasBackendErrorCode(error: unknown, code: string): boolean {
  return backendErrorCodes(error).includes(code);
}

/**
 * Espera antes del ÚNICO reintento del `errorLink` ante un código transitorio
 * (diagnóstico DBG del 15-oct). Reintentar al instante caía en la misma
 * ventana en que la otra transacción seguía reteniendo los catálogos del
 * tenant (clasificación, concesión, carretera): fallaba dos veces seguidas.
 * El servidor ya reintenta con espera (~8 s); esto es lo que queda delante.
 */
export const TRANSIENT_RETRY_DELAY_MS = 600;

/**
 * `run()` (el `forward(operation)` del `errorLink`) pasados `ms`. Si quien
 * esperaba la respuesta se va antes (se desuscribe), no se reenvía nada.
 */
export function forwardAfter<T>(
  ms: number,
  run: () => Observable<T>
): Observable<T> {
  return new Observable<T>((observer) => {
    let inner: { unsubscribe: () => void } | null = null;
    const timer = setTimeout(() => {
      inner = run().subscribe(observer);
    }, ms);
    return () => {
      clearTimeout(timer);
      inner?.unsubscribe();
    };
  });
}
